import { Inject, Injectable, OnDestroy, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, of, ReplaySubject, combineLatest } from 'rxjs';
import { filter, map, take, takeUntil, catchError, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { AppState } from '../app.state';
import { WINDOW } from '../helpers/custom-window';
import { JWT } from '../types/jwt';
import { NcmUserRole, NcmUserType } from '../types/ncmUserType';
import { Role } from '../types/role';
import { LocationListState } from '../types/states/locationListState';
import { Auth0WrapperService } from './auth0-wrapper.service';

/**
 * This service provides Auth0 authentication functionality integrated with the existing application.
 * It maintains backward compatibility while using Auth0 Angular SDK directly.
 */
@Injectable()
export class AuthService implements OnDestroy {
  // Maintain original observables for backward compatibility
  loggedInEvent$: Subject<boolean> = new Subject<boolean>();
  loggedOutEvent$: Subject<void> = new Subject<void>();
  rolesChanged$: Subject<boolean> = new Subject<boolean>();
  /**
   * Emits true when the enriched token (with NCM claims) is available in localStorage
   */
  enrichedTokenReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  refreshHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Auth0 facade functionality
  private inFlightToken$: Observable<string | null> = null;
  private _isReady$ = new ReplaySubject<boolean>(1);
  private ncmUser: boolean = false; // Indicates if user is a valid NCM user

  // User profile information from Auth0
  private userProfile$ = new BehaviorSubject<any>(null);

  shouldFallback: boolean = false;
  cachedJwt: JWT = null;
  cacheTime: number = 0;
  disconnect$: Subject<boolean> = new Subject<boolean>();
  loggedIn: boolean;
  locationList: LocationListState;

  /**
   * Observable that indicates when the Auth0 SDK is ready
   */
  public readonly isReady$ = this._isReady$.asObservable();

  /**
   * Observable stream of the authenticated user profile
   */
  public get user$(): Observable<any> {
    return this.auth0Service.user$;
  }

  /**
   * Observable stream of the authentication state
   */
  public get isAuthenticated$(): Observable<boolean> {
    return this.auth0Service.isAuthenticated$;
  }

  private auth0Service: Auth0Service;

  constructor(
    public jwtHelper: JwtHelperService,
    private injector: Injector,
    private router: Router,
    private store: Store<AppState>,
    private auth0WrapperService: Auth0WrapperService,
    @Inject(WINDOW) private window: Window
  ) {
    this.auth0Service = this.auth0WrapperService.getInstance();

    // Mark the service as ready once we've confirmed the SDK is initialized
    this.auth0Service.isAuthenticated$.subscribe({
      next: () => this._isReady$.next(true),
      error: () => this._isReady$.next(true) // Even on error, mark as ready
    });

    // Initialize user profile from Auth0Service
    this.auth0Service.user$
      .pipe(
        filter(user => !!user),
        takeUntil(this.disconnect$)
      )
      .subscribe(user => {
        this.userProfile$.next(user);
        this.loggedInEvent$.next(true);
      });

    // Keep original event behavior for backward compatibility
    this.loggedInEvent$
      .pipe(takeUntil(this.disconnect$))
      .subscribe(_ => {
        this.loggedIn = true;
      });

    this.loggedOutEvent$
      .pipe(takeUntil(this.disconnect$))
      .subscribe(_ => {
        this.logout();
      });

    combineLatest([
      this.auth0Service.isAuthenticated$,
      this.auth0Service.user$
    ])
      .pipe(
        takeUntil(this.disconnect$),
        filter(([isAuthenticated, user]) => isAuthenticated && !!user),
        take(1) // Only need to fetch once per login
      )
      .subscribe(([isAuthenticated, user]) => {
        // On login, fetch enriched tokens from backend
        if (!localStorage.getItem('token')) {
          this.getEnrichedTokensFromBackend();
        } else {
          const decoded = this.jwtHelper.decodeToken(localStorage.getItem('token'));
          if (decoded.ncmUserType && decoded.ncmUpsRole) {
            const ncmUserType = decoded.ncmUserType;
            const ncmUpsRole = decoded.ncmUpsRole;
            this.ncmUser = ncmUserType === NcmUserType.Carrier || ncmUserType === NcmUserType.Customer ||
              (ncmUserType === NcmUserType.Upser && (ncmUpsRole === NcmUserRole.CSR || ncmUpsRole === NcmUserRole.Admin));
            this.refreshHeader$.next(true);
          }
        }
      });

    // Keep location list management as before
    this.store
      .select(s => s.locationList)
      .pipe(takeUntil(this.disconnect$))
      .subscribe(locationList => {
        this.locationList = locationList;
        this.rolesChanged$.next(true);
      });
  }

  ngOnDestroy() {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();
  }

  /**
   * Gets the access token for the authenticated user
   * @returns An observable that emits the access token or null if not authenticated
   */
  getAccessToken$(): Observable<string | null> {
    if (!this.inFlightToken$) {
      // Skip the token call for signed-out users: getAccessTokenSilently() would open a
      // hidden /authorize iframe per HTTP request, each logged by Auth0 as a Silent
      // Authentication event. isAuthenticated$ only emits once the SDK has finished loading.
      this.inFlightToken$ = this.auth0Service.isAuthenticated$.pipe(
        take(1),
        switchMap(isAuthenticated => isAuthenticated ? this.auth0Service.getAccessTokenSilently() : of(null)),
        map(token => (token ? token : null)),
        catchError(() => of(null)),
        shareReplay(1),
        tap({
          complete: () => {
            this.inFlightToken$ = null;
          },
          error: () => {
            this.inFlightToken$ = null;
          }
        })
      );
    }
    return this.inFlightToken$;
  }

  /**
   * Redirects the user to the Auth0 login page
   * @param options Optional configuration for the redirect
   */
  loginRedirect(options?: any) {
    // Ensure we use the configured redirectUri
    const loginOptions = {
      ...options,
      authorizationParams: {
        ...options?.authorizationParams,
        redirect_uri: options?.authorizationParams?.redirect_uri || this.window.location.origin + '/gateway'
      }
    };

    this.auth0Service.loginWithRedirect(loginOptions);
  }

  /**
   * Handle Auth0 callback after redirect from login
   * Call this method on your callback/gateway component to process the authorization code
   * @returns Observable that completes when callback is processed
   */
  handleCallback(): Observable<any> {
    // Check if we have callback parameters (code/state)
    const urlParams = new URLSearchParams(this.window.location.search);
    const hasCode = urlParams.has('code');
    const hasState = urlParams.has('state');

    if (!hasCode || !hasState) {
      console.log('No Auth0 callback parameters found');
      return of(null);
    }

    console.log('Processing Auth0 callback...');

    // For standard Auth0 flow, we need to let the SDK process the callback
    console.log('Standard Auth0 flow - waiting for SDK to process callback');

    // Return an observable that waits for authentication state to resolve (either true or false)
    return this.isAuthenticated$.pipe(
      take(1), // Take the first emission (whether true or false)
      timeout(10000), // 10 second timeout
      catchError(error => {
        console.error('Auth0 callback processing timeout or error:', error);
        return of(false);
      }),
      tap(result => console.log('Callback processing completed, authenticated:', result))
    );
  }

  // ...
  /**
   * Check if user is authenticated - combines Auth0 state with token availability
   */
  public isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    // Special case where token was set as invalid undefined string
    if (token === 'undefined') {
      console.log('Invalid JWT value of \'undefined\'. Logging out.');
      this.logout();
      return false;
    }

    if (token === null) {
      // If no enriched token yet, check Auth0 state
      return this.loggedIn;
    }

    // Check whether the token is expired and return true or false
    return !this.jwtHelper.isTokenExpired(token);
  }

  // This method is now available as a getter above

  public hasRole(role: Role) {
    return this.locationList?.locations?.some(l => (l.role & role) === role) ?? false;
  }

  public hasLocationRole(slic: string, role: Role) {
    return this.locationList?.locations?.some(l => l.slicName === slic && (l.role & role) === role) ?? false;
  }

  public hasLocation(slic: string) {
    return this.locationList?.locations?.some(l => l.slicName === slic) ?? false;
  }

  /**
   * Check if user has a valid token - delegates to Auth0
   */
  public hasValidRefreshToken(): boolean {
    // In Auth0, we don't need to check refresh tokens manually
    // This is handled by the SDK
    return this.loggedIn;
  }

  /**
   * Environment detection - uses enriched JWT token (original implementation)
   */
  public isProduction() {
    try {
      return this.getJwt()?.isProduction;
    }
    catch (err) {
      console.log("AuthServce Error: ", err.message);
    }
    return true;
  }

  /**
   * Get user's full name from Auth0 profile
   */
  public getFullName() {
    const user = this.userProfile$.getValue();
    return user?.name || '';
  }

    /**
   * Get user's unique identifier from Auth0 profile
   */
  public getUniqueName() {
    const user = this.userProfile$.getValue();
    return user?.email || user?.nickname || '';
  }

  /**
   * Get user's unique identifier from Auth0 profile
   * Get user's email from JWT
   * Get user's unique name while updating
   */
  public getUniqueNameWhileUpdate() {
    const user = this.userProfile$.getValue();
    const token = localStorage.getItem('token');
    const jwtUser = this.jwtHelper.decodeToken(token);
    return jwtUser?.unique_name || user?.name || user?.email || user?.nickname || '';
  }

  /**
   * Get the user's UPS ID from the profile
   * This will explicitly extract the uid from the ID token
   */
  public getUpsId() {
    const user = this.userProfile$.getValue();
    // Attempt to get the uid claim which is what the backend expects
    return user?.uid || user?.sub?.split('|')[1] || user?.sub || '';
  }

  /**
   * Check if user is NCM user - uses enriched JWT token (original implementation)
   */
  public isNcmUser() {
    return this.ncmUser;
  }

  /**
   * Delegate login to TmsAuth
   */
  public login(username?, password?) {
    return of(null);
  }

  /**
   * Handle Auth0 callback - not needed as Auth0 SDK handles this automatically
   * Kept for backward compatibility
   */
  public authorize(code, state) {
    return Promise.resolve(null);
  }

  /**
   * Get enriched tokens from backend after Auth0 authentication
   */
  private async getEnrichedTokensFromBackend() {
    try {
      const user = this.userProfile$.getValue();
      if (!user) {
        return;
      }
      // Get Auth0 access token to authenticate with backend
      const auth0Token = await this.auth0Service.getAccessTokenSilently().toPromise();
      const response = await fetch('/api/user/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth0Token}`
        },
        body: JSON.stringify({
          username: user.email || user.nickname || '',
          password: 'Auth0' // Dummy password, not used by backend
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.token) {
          localStorage.setItem('token', data.token);
          const decoded = this.jwtHelper.decodeToken(data.token);
          if (decoded.ncmUserType && decoded.ncmUpsRole) {
            const ncmUserType = decoded.ncmUserType;
            const ncmUpsRole = decoded.ncmUpsRole;
            this.ncmUser = ncmUserType === NcmUserType.Carrier || ncmUserType === NcmUserType.Customer ||
              (ncmUserType === NcmUserType.Upser && (ncmUpsRole === NcmUserRole.CSR || ncmUpsRole === NcmUserRole.Admin));
            this.refreshHeader$.next(true);
          }
          if (data.refresh) {
            localStorage.setItem('refresh', data.refresh);
          }

          this.enrichedTokenReady$.next(true);
        } else if (response.status === 401 || response.status === 403) {
          // Not a valid TRS user, redirect to /no-locations
          this.router.navigate(['/no-locations']);
        } else {
          console.error('Failed to get enriched tokens from backend:', response.status, response.statusText);
        }
      }
    } catch (error) {
      console.error('Error getting enriched tokens from backend:', error);
    }
  }

  /**
   * Report metrics on login
   */
  private reportLoginMetrics() {
    let offset = new Date().getTimezoneOffset() * -1 / 60; // converts minutes to hours

    // Use injector to avoid circular dependency with ApiService
    setTimeout(() => {
      try {
        // Get ApiService using injector to avoid circular dependency
        const apiService = this.injector.get('ApiService' as any) as any;
        if (apiService && typeof apiService.logMetrics === 'function') {
          apiService.logMetrics({ timezoneOffset: offset })
            .pipe(take(1))
            .subscribe(
              _ => { console.log('User metrics logged.'); },
              err => { console.log(`Error logging user metrics. ${JSON.stringify(err ?? '')}`); }
            );
        }
      } catch (err) {
        console.log('Could not log user metrics:', err);
      }
    }, 0);
  }

  /**
   * Get JWT claims - uses enriched token from backend (original implementation logic)
   */
  public getJwt(): JWT {
    if (!this.cachedJwt || (Date.now() - this.cacheTime) > 1000 * 5) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          this.cachedJwt = this.jwtHelper.decodeToken(token);
        } else {
          // Fallback to Auth0 user info if no enriched token available yet
          const user = this.userProfile$.getValue();
          if (user) {
            this.cachedJwt = {
              trsUserId: user.sub,
              fullName: user.name,
              unique_name: user.email,
              isProduction: true, // Default until enriched token is available
              ncmUserType: null,
              ncmUpsRole: null
            } as JWT;
          } else {
            this.cachedJwt = null;
          }
        }
      } catch (err) {
        console.log('Invalid JWT value. Logging out. Error: ', err.message);
        this.logout();
      }

      this.cacheTime = Date.now();
    }

    // Force logout if userId not contained in the JWT or if JWT has been removed.
    // Also, force logout if roles is still contained in JWT
    if (this.cachedJwt && (!this.cachedJwt?.trsUserId || this.cachedJwt?.roles)) {
      this.logout();
    }

    return this.cachedJwt;
  }

  /**
   * Handle response after login - kept for backward compatibility
   * but not actually used with Auth0
   */
  private handleRequestPostResponse(response) {
    this.loggedInEvent$.next(true);
  }

  /**
   * Handle error response - kept for backward compatibility
   */
  private handleRequestErrorResponse(errors) {
    console.log(errors);
  }

  /**
   * Logout user - clears session and redirects to gateway with logout flag
   */
  public logout() {
    // Clear any application-specific storage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');

    // Clear Auth0 session data
    this.userProfile$.next(null);
    this.cachedJwt = null;
    this.cacheTime = 0;
    this.loggedIn = false;

    // Set a flag to indicate this is a logout scenario
    sessionStorage.setItem('isLogout', 'true');


    this.auth0Service.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/gateway`
      }
    });
  }

}
